// The organisation's secrets, metadata only: the API never returns a value or ciphertext.
// Layout from design/isomorphic/src/pages/secrets.tsx.
import { ArrowsClockwise, Key, ListBullets, LockKey, PencilSimple, Trash } from "@phosphor-icons/react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ActionIcon, Button, Tooltip } from "rizzui";

import { type Messages, useT } from "../i18n";
import { ago } from "../pages/format";
import { type Column, DataTable } from "../ui/DataTable";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { TemplateIcon } from "./TemplatePicker";
import { type Credential, scopeSummary, templateOf } from "./templates";

/** The changes, which need this browser's device signature. */
export interface SignedActions {
  edit: (c: Credential) => void;
  replace: (c: Credential) => void;
  remove: (c: Credential) => void;
}

interface Props {
  query: UseQueryResult<Credential[], Error>;
  onUsage: (c: Credential) => void;
  /** Absent when this browser cannot sign (not linked): the list is read-only. */
  actions?: SignedActions;
}

function Action({ label, tip, onClick, danger, children }: { label: string; tip: string; onClick: () => void; danger?: boolean; children: ReactNode }) {
  return (
    <Tooltip content={tip} placement="top">
      <ActionIcon size="sm" variant="outline" aria-label={label} onClick={onClick} className={danger ? "hover:border-red hover:text-red" : undefined}>
        {children}
      </ActionIcon>
    </Tooltip>
  );
}

/** Edit, labelled; without a device to sign it stays on the row, disabled, and says why. */
function EditButton({ t, credential, actions }: { t: Messages["secrets"]["table"]; credential: Credential; actions: SignedActions | undefined }) {
  const button = (
    <Button
      size="sm"
      variant="outline"
      className="gap-1.5 px-2.5"
      aria-label={t.editLabel(credential.name)}
      disabled={!actions}
      onClick={() => actions?.edit(credential)}
    >
      <PencilSimple weight="bold" className="h-4 w-4" />
      <span className="hidden sm:inline">{t.edit}</span>
    </Button>
  );
  if (actions) return button;
  return (
    <Tooltip content={t.editUnlinked} placement="top">
      <span tabIndex={0} aria-label={t.editUnlinked}>
        {button}
      </span>
    </Tooltip>
  );
}

function columns(t: Messages["secrets"]["table"], deleteTip: string, onUsage: Props["onUsage"], actions: SignedActions | undefined): Column<Credential>[] {
  const holder: Partial<Record<string, string>> = t.holder;
  return [
    {
      key: "name",
      header: t.name,
      cell: (c) => (
        <div className="flex items-center gap-3">
          <TemplateIcon id={templateOf(c)?.id} className="hidden sm:flex" />
          <div className="min-w-0">
            {actions ? (
              <button
                type="button"
                className="block max-w-full truncate font-mono text-[13px] font-semibold text-gray-900 hover:text-primary"
                onClick={() => actions.edit(c)}
              >
                {c.name}
              </button>
            ) : (
              <div className="truncate font-mono text-[13px] font-semibold text-gray-900">{c.name}</div>
            )}
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
              <span className="truncate">{templateOf(c)?.label ?? c.kind}</span>
              {c.worker_exposed ? (
                <Pill tone="red" className="px-1.5 py-0 text-[10.5px] 2xl:hidden">
                  {t.agentCanRead}
                </Pill>
              ) : null}
            </div>
          </div>
        </div>
      ),
    },
    { key: "value", header: t.value, hideBelow: "2xl", cell: () => (
        <>
          <span className="font-mono tracking-[0.2em] text-gray-400" aria-hidden>
            ••••••••
          </span>
          <span className="sr-only">{t.writeOnly}</span>
        </>
      ),
    },
    { key: "scope", header: t.usableFor, hideBelow: "md", cell: (c) => <Mono className="whitespace-normal">{scopeSummary(c)}</Mono> },
    {
      key: "kept",
      header: t.kept,
      hideBelow: "2xl",
      cell: (c) => (
        <div className="flex flex-wrap gap-1.5">
          <Pill tone="secondary" icon={<LockKey weight="duotone" />}>
            {holder[c.holder] ?? c.holder}
          </Pill>
          {c.worker_exposed ? <Pill tone="red">{t.agentCanRead}</Pill> : null}
        </div>
      ),
    },
    { key: "used", header: t.lastUsed, hideBelow: "sm", cell: (c) => <span className="whitespace-nowrap">{c.last_used_at ? ago(c.last_used_at) : t.never}</span> },
    {
      key: "actions",
      header: "",
      className: "text-end",
      cell: (c) => (
        <div className="flex justify-end gap-1 sm:gap-1.5">
          <EditButton t={t} credential={c} actions={actions} />
          <Action label={t.usage} tip={t.usageTip} onClick={() => onUsage(c)}>
            <ListBullets weight="bold" className="h-4 w-4" />
          </Action>
          {actions ? (
            <>
              <Action label={t.replace} tip={t.replace} onClick={() => actions.replace(c)}>
                <ArrowsClockwise weight="bold" className="h-4 w-4" />
              </Action>
              <Action label={t.deleteLabel(c.name)} tip={deleteTip} danger onClick={() => actions.remove(c)}>
                <Trash weight="bold" className="h-4 w-4" />
              </Action>
            </>
          ) : null}
        </div>
      ),
    },
  ];
}

export function SecretsTable({ query, onUsage, actions }: Props) {
  const { common, secrets } = useT();
  const t = secrets.table;
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState title={t.loadError} message={query.error.message} />;
  if (query.data.length === 0) {
    return (
      <EmptyState compact icon={<Key weight="duotone" />} title={t.empty}>
        {t.emptyBody}
      </EmptyState>
    );
  }
  return <DataTable label={t.label} columns={columns(t, common.actions.delete, onUsage, actions)} rows={query.data} rowKey={(c) => c.id} />;
}
